import React, { useState, useEffect, useCallback } from 'react';
import { Tabs, Tab } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import LoadingSpinner from '../components/LoadingSpinner';
import DOMPurify from 'dompurify';
import Swal from 'sweetalert2';
import i18n from '../i18n';
import { Link, useNavigate } from 'react-router-dom';
import '../css/news.css';
import '../css/news-detail.css';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarAlt, faClock, faUser } from "@fortawesome/free-regular-svg-icons";
import { faArrowRight, faBookOpen, faTrash, faEdit } from "@fortawesome/free-solid-svg-icons";
import RichTextEditor from './RichTextEditor';
import ReactPaginate from "react-paginate";
import api from "../api";

const backgrounds = [
    '/images/news-defaults/gatling.png', //gatling
    '/images/news-defaults/driver.png', //driver
    '/images/news-defaults/firearms_expert.png', //firearms
    '/images/news-defaults/gas_soldier.png', //gas soldier
    '/images/news-defaults/marine.png', //marine
    '/images/news-defaults/nuclear.png', //nuclear
    '/images/news-defaults/seeker.png', //seeker
    '/images/news-defaults/shotgun_master.png', //shotgun master
    '/images/news-defaults/sniper.png', //sniper
    '/images/news-defaults/strike_force.png', //strike force
    '/images/news-defaults/swat.png', //swat
    '/images/news-defaults/teleporter.png', //teleporter
    '/images/news-defaults/titan.png' //titan
];

const getCurrentDateTime = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const formatDateForInput = (dateString) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
};

function NewsTab({ loadingBarRef }) {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState('createNews');
    const [language, setLanguage] = useState(i18n.language);
    const [createNewsData, setCreateNewsData] = useState({
        date: getCurrentDateTime(),
        title: { en: '', ru: '', ua: '', ko: '', fr: '' },
        content: { en: '', ru: '', ua: '', ko: '', fr: '' }
    });
    const [editNewsData, setEditNewsData] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [newsList, setNewsList] = useState([]);
    const [selectedNewsLang, setSelectedNewsLang] = useState(i18n.language);
    const [showPreview, setShowPreview] = useState(false);
    const [showEditPreview, setShowEditPreview] = useState(false);
    const [media, setMedia] = useState(null);
    const [fileName, setFileName] = useState('');
    const [selectedGroupId, setSelectedGroupId] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [selectedNews, setSelectedNews] = useState(null);
    const [previewImageUrl, setPreviewImageUrl] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const limit = 10;
    const [importing, setImporting] = useState(false);
    const [drafts, setDrafts] = useState([]);
    const [draftsLoading, setDraftsLoading] = useState(false);
    const [publishingGroup, setPublishingGroup] = useState(null);
    const [translating, setTranslating] = useState(false);
    const [isDraftEdit, setIsDraftEdit] = useState(false);

    useEffect(() => {
        if (media) {
            const reader = new FileReader();
            reader.onload = (e) => {
                setPreviewImageUrl(e.target.result);
            };
            reader.readAsDataURL(media);
        } else {
            setPreviewImageUrl(null);
        }
    }, [media]);

    const handleDateChange = (e) => {
        if (activeTab === 'createNews') {
            setCreateNewsData({ ...createNewsData, date: e.target.value });
        } else if (isEditing) {
            setEditNewsData({ ...editNewsData, date: e.target.value });
        }
    };

    const handleChange = (field, value, lang) => {
        if (activeTab === 'createNews') {
            setCreateNewsData((prevData) => ({
                ...prevData,
                [field]: {
                    ...prevData[field],
                    [lang]: value
                }
            }));
        } else if (isEditing) {
            setEditNewsData((prevData) => ({
                ...prevData,
                [field]: {
                    ...prevData[field],
                    [lang]: value
                }
            }));
        }
    };

    const validateNewsFields = (newsData) => {
        const errors = [];
        for (const lang of ['en', 'ru', 'ua', 'ko', 'fr']) {
            if (!newsData.title[lang].trim()) {
                errors.push(t('news.validationError.title', { lang: t(`news.languages.${lang}`) }));
            }
            if (!newsData.content[lang].trim()) {
                errors.push(t('news.validationError.content', { lang: t(`news.languages.${lang}`) }));
            }
        }
        return errors;
    };

    const confirmSubmitNews = (e) => {
        e.preventDefault();
        const validationErrors = validateNewsFields(createNewsData);
        if (validationErrors.length > 0) {
            Swal.fire({
                title: t('news.validationError.titleHeader'),
                html: `<ul>${validationErrors.map(err => `<li>${err}</li>`).join('')}</ul>`,
                icon: 'error',
                confirmButtonText: t('posts.ok'),
                background: '#1a1a1a',
                color: '#b3b3b3',
                iconColor: '#ff0000',
                customClass: {
                    popup: 'swal2-dark-popup',
                    confirmButton: 'swal2-dark-button',
                }
            });
            return;
        }
        Swal.fire({
            text: t('news.confirmSubmit'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('posts.ok'),
            cancelButtonText: t('posts.cancel'),
            background: '#1a1a1a',
            color: '#b3b3b3',
            iconColor: '#ffcc00',
            customClass: {
                popup: 'swal2-dark-popup',
                confirmButton: 'swal2-dark-button',
                cancelButton: 'swal2-dark-button'
            }
        }).then((result) => {
            if (result.isConfirmed) {
                submitNews(false);
            }
        });
    };

    const confirmEditNews = (e) => {
        e.preventDefault();
        const validationErrors = validateNewsFields(editNewsData);
        if (validationErrors.length > 0) {
            Swal.fire({
                title: t('news.validationError.titleHeader'),
                html: `<ul>${validationErrors.map(err => `<li>${err}</li>`).join('')}</ul>`,
                icon: 'error',
                confirmButtonText: t('posts.ok'),
                background: '#1a1a1a',
                color: '#b3b3b3',
                iconColor: '#ff0000',
                customClass: {
                    popup: 'swal2-dark-popup',
                    confirmButton: 'swal2-dark-button',
                }
            });
            return;
        }
        Swal.fire({
            text: t('news.confirmEdit'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('posts.ok'),
            cancelButtonText: t('posts.cancel'),
            background: '#1a1a1a',
            color: '#b3b3b3',
            iconColor: '#ffcc00',
            customClass: {
                popup: 'swal2-dark-popup',
                confirmButton: 'swal2-dark-button',
                cancelButton: 'swal2-dark-button'
            }
        }).then((result) => {
            if (result.isConfirmed) {
                handleEditNews();
            }
        });
    };

    const submitNews = async (draft = false) => {
        setIsSubmitting(true);
        if (loadingBarRef.current) {
            loadingBarRef.current.continuousStart();
        }
        const sanitizedContent = {
            en: DOMPurify.sanitize(createNewsData.content.en),
            ru: DOMPurify.sanitize(createNewsData.content.ru),
            ua: DOMPurify.sanitize(createNewsData.content.ua),
            ko: DOMPurify.sanitize(createNewsData.content.ko),
            fr: DOMPurify.sanitize(createNewsData.content.fr),
        };
        const sanitizedNews = { ...createNewsData, content: sanitizedContent, draft };
        const formData = new FormData();
        if (media) {
            formData.append('image', media);
        }
        formData.append('newsData', JSON.stringify(sanitizedNews));
        try {
            const accessToken = localStorage.getItem('access_token');
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/news`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${accessToken}` },
                body: formData
            });
            if (response.ok) {
                Swal.fire({
                    text: draft ? t('news.draftSaved') : t('news.success'),
                    icon: 'success',
                    background: '#1a1a1a',
                    color: '#b3b3b3',
                    iconColor: '#00ff00',
                    confirmButtonColor: '#3085d6',
                    confirmButtonText: t('posts.ok'),
                    customClass: {
                        popup: 'swal2-dark-popup',
                        confirmButton: 'swal2-dark-button'
                    }
                });
                setCreateNewsData({
                    date: getCurrentDateTime(),
                    title: { en: '', ru: '', ua: '', ko: '', fr: '' },
                    content: { en: '', ru: '', ua: '', ko: '', fr: '' }
                });
                setShowPreview(false);
                setMedia(null);
                setFileName('');
            } else {
                throw new Error('Failed to submit news');
            }
        } catch (error) {
            Swal.fire({
                text: draft ? t('news.draftError') : t('news.error'),
                icon: 'error',
                background: '#1a1a1a',
                color: '#b3b3b3',
                iconColor: '#ff0000',
                confirmButtonColor: '#3085d6',
                confirmButtonText: t('posts.ok'),
                customClass: {
                    popup: 'swal2-dark-popup',
                    confirmButton: 'swal2-dark-button'
                }
            });
        } finally {
            setIsSubmitting(false);
            if (loadingBarRef.current) {
                loadingBarRef.current.complete();
            }
        }
    };

    const handleSaveDraft = () => {
        if (!createNewsData.title.en.trim()) {
            Swal.fire({
                text: t('news.validationError.title', { lang: t('news.languages.en') }),
                icon: 'warning',
                background: '#1a1a1a',
                color: '#b3b3b3',
                iconColor: '#ffcc00',
                confirmButtonColor: '#3085d6',
                confirmButtonText: t('posts.ok'),
                customClass: {
                    popup: 'swal2-dark-popup',
                    confirmButton: 'swal2-dark-button'
                }
            });
            return;
        }
        submitNews(true);
    };

    const fetchNewsList = useCallback(async () => {
        setIsLoading(true);
        if (loadingBarRef.current) {
            loadingBarRef.current.continuousStart();
        }
        try {
            const response = await api.get(
                `${process.env.REACT_APP_API_URL}/api/news?lang=${selectedNewsLang}&page=${page}&limit=${limit}`,
                {
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
                }
            );
            setNewsList(response.data.news);
            setTotalPages(Math.ceil(response.data.total / limit));
        } catch (error) {
            console.error('Error fetching news:', error);
        } finally {
            setIsLoading(false);
            if (loadingBarRef.current) {
                loadingBarRef.current.complete();
            }
        }
    }, [loadingBarRef, selectedNewsLang, page, limit]);

    useEffect(() => {
        if (activeTab === 'manageNews') {
            setPage(1);
        }
    }, [activeTab, selectedNewsLang]);

    const handleSelectNews = async (groupId) => {
        try {
            const response = await api.get(`${process.env.REACT_APP_API_URL}/api/news/group/${groupId}`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
            });
            const newsGroup = response.data;
            const titles = { en: '', ru: '', ua: '', ko: '', fr: '' };
            const contents = { en: '', ru: '', ua: '', ko: '', fr: '' };
            newsGroup.forEach(newsItem => {
                const lang = newsItem.lang;
                if (lang && titles.hasOwnProperty(lang)) {
                    titles[lang] = newsItem.title;
                    contents[lang] = newsItem.description;
                }
            });
            const formattedDate = formatDateForInput(newsGroup[0].createdAt.date);
            setEditNewsData({
                date: formattedDate,
                title: titles,
                content: contents
            });
            setSelectedGroupId(groupId);
            setSelectedNews(newsGroup[0]);
            setIsEditing(true);
            setFileName(newsGroup[0].image ? newsGroup[0].image.split('/').pop() : '');
            setShowEditPreview(false);
        } catch (error) {
            Swal.fire({
                text: t('news.fetchError'),
                icon: 'error',
                background: '#1a1a1a',
                color: '#b3b3b3',
                iconColor: '#ff0000',
                confirmButtonColor: '#3085d6',
                confirmButtonText: t('posts.ok'),
                customClass: {
                    popup: 'swal2-dark-popup',
                    confirmButton: 'swal2-dark-button'
                }
            });
        }
    };

    const handleEditNews = async () => {
        setIsSubmitting(true);
        if (loadingBarRef.current) {
            loadingBarRef.current.continuousStart();
        }
        const sanitizedContent = {
            en: DOMPurify.sanitize(editNewsData.content.en),
            ru: DOMPurify.sanitize(editNewsData.content.ru),
            ua: DOMPurify.sanitize(editNewsData.content.ua),
            ko: DOMPurify.sanitize(editNewsData.content.ko),
            fr: DOMPurify.sanitize(editNewsData.content.fr),
        };
        const sanitizedNews = { ...editNewsData, content: sanitizedContent };
        const formData = new FormData();
        if (media) {
            formData.append('image', media);
        }
        const newsData = {
            date: sanitizedNews.date,
            titles: sanitizedNews.title,
            contents: sanitizedNews.content
        };
        formData.append('newsData', JSON.stringify(newsData));
        try {
            const accessToken = localStorage.getItem('access_token');
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/news/group/${selectedGroupId}`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${accessToken}` },
                body: formData
            });
            if (response.ok) {
                Swal.fire({
                    text: t('news.updateSuccess'),
                    icon: 'success',
                    background: '#1a1a1a',
                    color: '#b3b3b3',
                    iconColor: '#00ff00',
                    confirmButtonColor: '#3085d6',
                    confirmButtonText: t('posts.ok'),
                    customClass: {
                        popup: 'swal2-dark-popup',
                        confirmButton: 'swal2-dark-button'
                    }
                });
            } else {
                throw new Error('Failed to update news');
            }
            fetchNewsList();
            setIsEditing(false);
            setSelectedGroupId(null);
            setSelectedNews(null);
            setEditNewsData(null);
            setMedia(null);
            setFileName('');
            setShowEditPreview(false);
        } catch (error) {
            Swal.fire({
                text: t('news.updateError'),
                icon: 'error',
                background: '#1a1a1a',
                color: '#b3b3b3',
                iconColor: '#ff0000',
                confirmButtonColor: '#3085d6',
                confirmButtonText: t('posts.ok'),
                customClass: {
                    popup: 'swal2-dark-popup',
                    confirmButton: 'swal2-dark-button'
                }
            });
        } finally {
            setIsSubmitting(false);
            if (loadingBarRef.current) {
                loadingBarRef.current.complete();
            }
        }
    };

    useEffect(() => {
        setLanguage(i18n.language);
        setSelectedNewsLang(i18n.language);
    }, [i18n.language]);

    useEffect(() => {
        if (activeTab === 'manageNews') {
            fetchNewsList();
        }
    }, [activeTab, selectedNewsLang, fetchNewsList]);

    const confirmDeleteNews = async (groupId) => {
        Swal.fire({
            text: t('news.confirmDelete'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('posts.ok'),
            cancelButtonText: t('posts.cancel'),
            background: '#1a1a1a',
            color: '#b3b3b3',
            iconColor: '#ff0000',
            customClass: {
                popup: 'swal2-dark-popup',
                confirmButton: 'swal2-dark-button',
                cancelButton: 'swal2-dark-button'
            }
        }).then(async (result) => {
            if (result.isConfirmed) {
                await handleDeleteNews(groupId);
            }
        });
    };

    const handleDeleteNews = async (groupId) => {
        if (loadingBarRef.current) {
            loadingBarRef.current.continuousStart();
        }
        try {
            const accessToken = localStorage.getItem('access_token');
            await api.delete(`${process.env.REACT_APP_API_URL}/api/admin/news/${groupId}`, {
                headers: {
                    'Authorization': `Bearer ${accessToken}`
                }
            });
            Swal.fire({
                text: t('news.deleteSuccess'),
                icon: 'success',
                background: '#1a1a1a',
                color: '#b3b3b3',
                iconColor: '#00ff00',
                confirmButtonColor: '#3085d6',
                confirmButtonText: t('posts.ok'),
                customClass: {
                    popup: 'swal2-dark-popup',
                    confirmButton: 'swal2-dark-button'
                }
            });
            setNewsList(newsList.filter(news => news.group_id !== groupId));
        } catch (error) {
            Swal.fire({
                text: t('news.deleteError'),
                icon: 'error',
                background: '#1a1a1a',
                color: '#b3b3b3',
                iconColor: '#ff0000',
                confirmButtonColor: '#3085d6',
                confirmButtonText: t('posts.ok'),
                customClass: {
                    popup: 'swal2-dark-popup',
                    confirmButton: 'swal2-dark-button'
                }
            });
        } finally {
            if (loadingBarRef.current) {
                loadingBarRef.current.complete();
            }
        }
    };

    const handleImport = async () => {
        setImporting(true);
        if (loadingBarRef.current) {
            loadingBarRef.current.continuousStart();
        }
        try {
            const response = await api.post(`${process.env.REACT_APP_API_URL}/api/admin/news/import`);
            const { added, skipped, errors } = response.data;
            let text = t('news.importSuccess', { added, skipped });
            if (errors && errors.length > 0) {
                text += `\n\n${errors.slice(0, 5).join('\n')}`;
            }
            Swal.fire({
                html: text.replace(/\n/g, '<br>'),
                icon: 'success',
                background: '#1a1a1a',
                color: '#b3b3b3',
                iconColor: '#00ff00',
                confirmButtonColor: '#3085d6',
                confirmButtonText: t('posts.ok'),
                customClass: {
                    popup: 'swal2-dark-popup',
                    confirmButton: 'swal2-dark-button'
                }
            });
        } catch (error) {
            Swal.fire({
                text: t('news.importError'),
                icon: 'error',
                background: '#1a1a1a',
                color: '#b3b3b3',
                iconColor: '#ff0000',
                confirmButtonColor: '#3085d6',
                confirmButtonText: t('posts.ok'),
                customClass: {
                    popup: 'swal2-dark-popup',
                    confirmButton: 'swal2-dark-button'
                }
            });
        } finally {
            setImporting(false);
            if (loadingBarRef.current) {
                loadingBarRef.current.complete();
            }
        }
    };

    const fetchDrafts = useCallback(async () => {
        setDraftsLoading(true);
        try {
            const response = await api.get(`${process.env.REACT_APP_API_URL}/api/admin/news/drafts`);
            setDrafts(response.data);
        } catch (error) {
            console.error('Error fetching drafts:', error);
        } finally {
            setDraftsLoading(false);
        }
    }, []);

    useEffect(() => {
        if (activeTab === 'drafts') {
            fetchDrafts();
        }
    }, [activeTab, fetchDrafts]);

    const handleEditDraft = (draft) => {
        const titles = { en: '', ru: '', ua: '', ko: '', fr: '' };
        const contents = { en: '', ru: '', ua: '', ko: '', fr: '' };
        ['en', 'ru', 'ua', 'ko', 'fr'].forEach((lang) => {
            titles[lang] = draft.titles?.[lang] || '';
            contents[lang] = draft.descriptions?.[lang] || '';
        });
        setEditNewsData({
            date: formatDateForInput(draft.created_at),
            title: titles,
            content: contents
        });
        setSelectedGroupId(draft.group_id);
        setSelectedNews({ image: draft.image, author: draft.author });
        setFileName(draft.image ? draft.image.split('/').pop() : '');
        setMedia(null);
        setShowEditPreview(false);
        setIsDraftEdit(true);
        setIsEditing(true);
        setActiveTab('manageNews');
    };

    const handleTranslate = async () => {
        if (!editNewsData) return;
        setTranslating(true);
        if (loadingBarRef.current) {
            loadingBarRef.current.continuousStart();
        }
        try {
            const response = await api.post(`${process.env.REACT_APP_API_URL}/api/admin/news/translate`, {
                title: editNewsData.title.en,
                body: editNewsData.content.en
            });
            const translations = response.data || {};
            setEditNewsData((prev) => {
                const title = { ...prev.title };
                const content = { ...prev.content };
                ['ru', 'ua', 'ko', 'fr'].forEach((lang) => {
                    if (translations[lang]) {
                        title[lang] = translations[lang].title || '';
                        content[lang] = translations[lang].body || '';
                    }
                });
                return { ...prev, title, content };
            });
        } catch (error) {
            Swal.fire({
                text: t('news.translateError'),
                icon: 'error',
                background: '#1a1a1a',
                color: '#b3b3b3',
                iconColor: '#ff0000',
                confirmButtonColor: '#3085d6',
                confirmButtonText: t('posts.ok'),
                customClass: {
                    popup: 'swal2-dark-popup',
                    confirmButton: 'swal2-dark-button'
                }
            });
        } finally {
            setTranslating(false);
            if (loadingBarRef.current) {
                loadingBarRef.current.complete();
            }
        }
    };

    const handleTranslateCreate = async () => {
        if (!createNewsData.title.en.trim()) {
            Swal.fire({
                text: t('news.validationError.title', { lang: t('news.languages.en') }),
                icon: 'warning',
                background: '#1a1a1a',
                color: '#b3b3b3',
                iconColor: '#ffcc00',
                confirmButtonColor: '#3085d6',
                confirmButtonText: t('posts.ok'),
                customClass: {
                    popup: 'swal2-dark-popup',
                    confirmButton: 'swal2-dark-button'
                }
            });
            return;
        }
        setTranslating(true);
        if (loadingBarRef.current) {
            loadingBarRef.current.continuousStart();
        }
        try {
            const response = await api.post(`${process.env.REACT_APP_API_URL}/api/admin/news/translate`, {
                title: createNewsData.title.en,
                body: createNewsData.content.en
            });
            const translations = response.data || {};
            setCreateNewsData((prev) => {
                const title = { ...prev.title };
                const content = { ...prev.content };
                ['ru', 'ua', 'ko', 'fr'].forEach((lang) => {
                    if (translations[lang]) {
                        title[lang] = translations[lang].title || '';
                        content[lang] = translations[lang].body || '';
                    }
                });
                return { ...prev, title, content };
            });
        } catch (error) {
            Swal.fire({
                text: t('news.translateError'),
                icon: 'error',
                background: '#1a1a1a',
                color: '#b3b3b3',
                iconColor: '#ff0000',
                confirmButtonColor: '#3085d6',
                confirmButtonText: t('posts.ok'),
                customClass: {
                    popup: 'swal2-dark-popup',
                    confirmButton: 'swal2-dark-button'
                }
            });
        } finally {
            setTranslating(false);
            if (loadingBarRef.current) {
                loadingBarRef.current.complete();
            }
        }
    };

    const handlePublishDraft = async () => {
        setIsSubmitting(true);
        setPublishingGroup(selectedGroupId);
        if (loadingBarRef.current) {
            loadingBarRef.current.continuousStart();
        }
        const sanitizedContent = {
            en: DOMPurify.sanitize(editNewsData.content.en),
            ru: DOMPurify.sanitize(editNewsData.content.ru),
            ua: DOMPurify.sanitize(editNewsData.content.ua),
            ko: DOMPurify.sanitize(editNewsData.content.ko),
            fr: DOMPurify.sanitize(editNewsData.content.fr),
        };
        try {
            await api.post(`${process.env.REACT_APP_API_URL}/api/admin/news/${selectedGroupId}/publish`, {
                titles: editNewsData.title,
                contents: sanitizedContent,
                date: editNewsData.date
            });
            Swal.fire({
                text: t('news.publishSuccess'),
                icon: 'success',
                background: '#1a1a1a',
                color: '#b3b3b3',
                iconColor: '#00ff00',
                confirmButtonColor: '#3085d6',
                confirmButtonText: t('posts.ok'),
                customClass: {
                    popup: 'swal2-dark-popup',
                    confirmButton: 'swal2-dark-button'
                }
            });
            setIsEditing(false);
            setIsDraftEdit(false);
            setSelectedGroupId(null);
            setEditNewsData(null);
            setMedia(null);
            setFileName('');
            setShowEditPreview(false);
        } catch (error) {
            Swal.fire({
                text: t('news.publishError'),
                icon: 'error',
                background: '#1a1a1a',
                color: '#b3b3b3',
                iconColor: '#ff0000',
                confirmButtonColor: '#3085d6',
                confirmButtonText: t('posts.ok'),
                customClass: {
                    popup: 'swal2-dark-popup',
                    confirmButton: 'swal2-dark-button'
                }
            });
        } finally {
            setIsSubmitting(false);
            setPublishingGroup(null);
            if (loadingBarRef.current) {
                loadingBarRef.current.complete();
            }
        }
    };

    const confirmPublishDraft = (e) => {
        e.preventDefault();
        Swal.fire({
            text: t('news.confirmSubmit'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('posts.ok'),
            cancelButtonText: t('posts.cancel'),
            background: '#1a1a1a',
            color: '#b3b3b3',
            iconColor: '#ffcc00',
            customClass: {
                popup: 'swal2-dark-popup',
                confirmButton: 'swal2-dark-button',
                cancelButton: 'swal2-dark-button'
            }
        }).then((result) => {
            if (result.isConfirmed) {
                handlePublishDraft();
            }
        });
    };

    const cancelEdit = () => {
        setIsEditing(false);
        setIsDraftEdit(false);
        setSelectedGroupId(null);
        setEditNewsData(null);
        setMedia(null);
        setFileName('');
        setShowEditPreview(false);
    };

    const handleDeleteDraft = (groupId) => {
        Swal.fire({
            text: t('news.confirmDelete'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('posts.ok'),
            cancelButtonText: t('posts.cancel'),
            background: '#1a1a1a',
            color: '#b3b3b3',
            iconColor: '#ff0000',
            customClass: {
                popup: 'swal2-dark-popup',
                confirmButton: 'swal2-dark-button',
                cancelButton: 'swal2-dark-button'
            }
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await api.delete(`${process.env.REACT_APP_API_URL}/api/admin/news/${groupId}`);
                    setDrafts(drafts.filter(d => d.group_id !== groupId));
                } catch (error) {
                    Swal.fire({
                        text: t('news.deleteError'),
                        icon: 'error',
                        background: '#1a1a1a',
                        color: '#b3b3b3',
                        iconColor: '#ff0000',
                        confirmButtonColor: '#3085d6',
                        confirmButtonText: t('posts.ok'),
                        customClass: {
                            popup: 'swal2-dark-popup',
                            confirmButton: 'swal2-dark-button'
                        }
                    });
                }
            }
        });
    };

    const getRandomBackgrounds = () => {
        const shuffledBackgrounds = [...backgrounds].sort(() => 0.5 - Math.random());
        return shuffledBackgrounds.slice(0, newsList.length);
    };
    const randomBackgrounds = getRandomBackgrounds();

    const handlePreview = () => {
        setShowPreview(true);
    };

    const handleBackToEditing = () => {
        setShowPreview(false);
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setMedia(file);
            setFileName(file.name);
        }
    };

    const handlePageClick = (event) => {
        setPage(event.selected + 1);
    };

    return (
        <div>
            <Tabs
                activeKey={activeTab}
                onSelect={(k) => {
                    setActiveTab(k);
                    if (k === 'createNews') {
                        setCreateNewsData({
                            date: getCurrentDateTime(),
                            title: { en: '', ru: '', ua: '', ko: '', fr: '' },
                            content: { en: '', ru: '', ua: '', ko: '', fr: '' }
                        });
                        setFileName('');
                        setMedia(null);
                        setShowPreview(false);
                    } else if (k === 'manageNews' && isEditing && selectedNews?.image) {
                        setFileName(selectedNews.image.split('/').pop());
                    }
                }}
                id="news-management-tabs"
                className="mb-3"
            >
                <Tab eventKey="createNews" title={t('news.createNews')}>
                    {!showPreview ? (
                        <form onSubmit={confirmSubmitNews}>
                            <div className="form-group">
                                <label>{t('news.date')}</label>
                                <input
                                    type="datetime-local"
                                    value={createNewsData.date}
                                    onChange={handleDateChange}
                                    required
                                />
                            </div>
                            <Tabs
                                activeKey={language}
                                onSelect={(k) => setLanguage(k)}
                                id="news-language-tabs"
                                className="mb-3"
                            >
                                {['en', 'ru', 'ua', 'ko', 'fr'].map((lang) => (
                                    <Tab eventKey={lang} title={t(`languages.${lang}`)} key={lang}>
                                        <div className="form-group">
                                            <label>{t('news.newsTitle')} ({t(`languages.${lang}`)}):</label>
                                            <input
                                                type="text"
                                                className="news-title-input"
                                                value={createNewsData.title[lang]}
                                                onChange={(e) => handleChange('title', e.target.value, lang)}
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>{t('news.content')} ({t(`languages.${lang}`)}):</label>
                                            <RichTextEditor
                                                value={createNewsData.content[lang]}
                                                onChange={(value) => handleChange('content', value, lang)}
                                            />
                                        </div>
                                    </Tab>
                                ))}
                            </Tabs>
                            <div>
                                {fileName && <span title={fileName}>{fileName}</span>}
                            </div>
                            <div className="form-buttons">
                                <button type="submit" className="submit-button" disabled={isSubmitting}>
                                    {isSubmitting ? t('news.adding') : t('admin.create')}
                                </button>
                                <button type="button" className="preview-button" onClick={handleSaveDraft} disabled={isSubmitting}>
                                    {t('news.saveDraft')}
                                </button>
                                <button type="button" className="preview-button" onClick={handleTranslateCreate} disabled={translating}>
                                    {translating ? t('news.translating') : t('news.translate')}
                                </button>
                                <label className="custom-file-upload">
                                    <input type="file" onChange={handleFileChange} />
                                    {t('posts.upload')}
                                </label>
                                <button type="button" className="preview-button" onClick={handlePreview}>
                                    {t('news.preview')}
                                </button>
                                <button type="button" className="preview-button" onClick={handleImport} disabled={importing}>
                                    {importing ? t('news.importing') : t('news.import')}
                                </button>
                            </div>
                        </form>
                    ) : (
                        <div className="preview-section">
                            <NewsItem
                                item={{
                                    title: createNewsData.title[language],
                                    description: createNewsData.content[language],
                                    created_at: createNewsData.date,
                                    author: 'admin',
                                    id: 'preview'
                                }}
                                background={previewImageUrl || backgrounds[Math.floor(Math.random() * backgrounds.length)]}
                                isPreview={true}
                            />
                            <button type="button" className="back-button" onClick={handleBackToEditing}>
                                {t('news.backToEditing')}
                            </button>
                        </div>
                    )}
                </Tab>
                <Tab eventKey="manageNews" title={t('news.manageNews')}>
                    {isEditing ? (
                        !showEditPreview ? (
                            <form onSubmit={isDraftEdit ? confirmPublishDraft : confirmEditNews}>
                                {selectedNews && selectedNews.image && (
                                    <div className="news-content" style={{justifyContent: 'center'}}>
                                        <div>
                                            <img src={selectedNews.image} alt="Current news image"
                                                 style={{maxWidth: '400px'}}/>
                                        </div>
                                    </div>
                                )}
                                <div className="form-group">
                                    <label>{t('news.date')}</label>
                                    <input
                                        type="datetime-local"
                                        value={editNewsData ? editNewsData.date : ''}
                                        onChange={handleDateChange}
                                        required
                                    />
                                </div>
                                <Tabs
                                    activeKey={language}
                                    onSelect={(k) => setLanguage(k)}
                                    id="news-language-tabs"
                                    className="mb-3"
                                >
                                    {['en', 'ru', 'ua', 'ko', 'fr'].map((lang) => (
                                        <Tab eventKey={lang} title={t(`languages.${lang}`)} key={lang}>
                                            <div className="form-group">
                                                <label>{t('news.newsTitle')} ({t(`languages.${lang}`)}):</label>
                                                <input
                                                    type="text"
                                                    className="news-title-input"
                                                    value={editNewsData ? editNewsData.title[lang] : ''}
                                                    onChange={(e) => handleChange('title', e.target.value, lang)}
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label>{t('news.content')} ({t(`languages.${lang}`)}):</label>
                                                <RichTextEditor
                                                    value={editNewsData ? editNewsData.content[lang] : ''}
                                                    onChange={(value) => handleChange('content', value, lang)}
                                                />
                                            </div>
                                        </Tab>
                                    ))}
                                </Tabs>
                                <div>
                                    {fileName && <span title={fileName}>{fileName}</span>}
                                </div>
                                <div className="form-buttons">
                                    {isDraftEdit ? (
                                        <>
                                            <button type="button" className="preview-button" onClick={handleTranslate} disabled={translating}>
                                                {translating ? t('news.translating') : t('news.translate')}
                                            </button>
                                            <button type="submit" className="submit-button" disabled={isSubmitting}>
                                                {isSubmitting ? t('news.publishing') : t('news.publish')}
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            <button type="submit" className="submit-button" disabled={isSubmitting}>
                                                {isSubmitting ? t('news.editing') : t('news.EditNews')}
                                            </button>
                                            <button type="button" className="preview-button" onClick={handleTranslate} disabled={translating}>
                                                {translating ? t('news.translating') : t('news.translate')}
                                            </button>
                                            <label className="custom-file-upload">
                                                <input type="file" onChange={handleFileChange}/>
                                                {t('posts.upload')}
                                            </label>
                                        </>
                                    )}
                                    <button type="button" className="preview-button" onClick={() => setShowEditPreview(true)}>
                                        {t('news.preview')}
                                    </button>
                                    <button type="button" className="edit-button" onClick={cancelEdit}>
                                        {t('news.cancelEditing')}
                                    </button>
                                </div>
                            </form>
                        ) : (
                            <div className="preview-section">
                                <h3 className="preview-label">{t('news.previewCard')}</h3>
                                <NewsItem
                                    item={{
                                        title: editNewsData.title[language],
                                        description: editNewsData.content[language],
                                        created_at: editNewsData.date,
                                        author: selectedNews ? selectedNews.author : 'admin',
                                        id: 'preview'
                                    }}
                                    background={previewImageUrl || (selectedNews && selectedNews.image) || backgrounds[Math.floor(Math.random() * backgrounds.length)]}
                                    isPreview={true}
                                />
                                <h3 className="preview-label">{t('news.previewDetail')}</h3>
                                <div className="news-detail-container">
                                    <div className="news-detail-banner">
                                        <div
                                            className="banner-background"
                                            style={{
                                                backgroundImage: `url(${previewImageUrl || (selectedNews && selectedNews.image) || ''})`
                                            }}
                                        ></div>
                                        <div className="banner-content">
                                            <div className="banner-category">{t('news.category', 'Update')}</div>
                                            <h1 className="banner-title">{editNewsData.title[language]}</h1>
                                            <div className="banner-meta">
                                                <span><FontAwesomeIcon icon={faCalendarAlt}/> {editNewsData.date}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <main className="news-detail-main">
                                        <div className="news-detail-body">
                                            <div
                                                dangerouslySetInnerHTML={{
                                                    __html: DOMPurify.sanitize(editNewsData.content[language])
                                                }}
                                            />
                                        </div>
                                    </main>
                                </div>
                                <button type="button" className="back-button" onClick={() => setShowEditPreview(false)}>
                                    {t('news.backToEditing')}
                                </button>
                            </div>
                        )
                    ) : (
                        <div className="news-list">
                            {isLoading ? (
                                <div className="text-center">
                                    <LoadingSpinner/>
                                    <p>{t('news.loading')}</p>
                                </div>
                            ) : newsList.length === 0 ? (
                                <p>{t('news.noNews')}</p>
                            ) : (
                                <>
                                    <div id="news-container-admin-page">
                                        {newsList.map((item, index) => {
                                            const background = item.image ? item.image : randomBackgrounds[index % randomBackgrounds.length];
                                            return (
                                                <NewsItem
                                                    key={item.id}
                                                    item={item}
                                                    background={background}
                                                    confirmDeleteNews={confirmDeleteNews}
                                                    handleSelectNews={() => handleSelectNews(item.group_id)}
                                                />
                                            );
                                        })}
                                    </div>
                                    {totalPages > 1 && (
                                        <ReactPaginate
                                            breakLabel="..."
                                            nextLabel={null}
                                            onPageChange={handlePageClick}
                                            pageRangeDisplayed={1}
                                            marginPagesDisplayed={2}
                                            pageCount={totalPages}
                                            previousLabel={null}
                                            forcePage={page - 1}
                                            containerClassName="pagination-container"
                                            pageClassName="pagination-item"
                                            pageLinkClassName="pagination-link"
                                            previousClassName="pagination-item"
                                            nextClassName="pagination-next"
                                            previousLinkClassName="pagination-previous"
                                            nextLinkClassName="pagination-link"
                                            breakClassName="pagination-item"
                                            breakLinkClassName="pagination-link"
                                            activeClassName="active"
                                        />
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </Tab>
                <Tab eventKey="drafts" title={t('news.drafts')}>
                    <div className="news-list">
                        {draftsLoading ? (
                            <div className="text-center">
                                <LoadingSpinner/>
                                <p>{t('news.loading')}</p>
                            </div>
                        ) : drafts.length === 0 ? (
                            <p>{t('news.noDrafts')}</p>
                        ) : (
                            drafts.map((draft) => {
                                const title = draft.titles[i18n.language] || draft.titles.en || draft.titles.ru || '';
                                return (
                                    <div className="draft-item" key={draft.group_id}>
                                        {draft.image && <img src={draft.image} alt="" className="draft-thumbnail"/>}
                                        <div className="draft-info">
                                            <span className="draft-title">{title}</span>
                                            <span className="draft-meta">{[draft.source, draft.created_at, draft.author].filter(Boolean).join(' · ')}</span>
                                        </div>
                                        <div className="draft-actions">
                                            <button
                                                className="submit-button"
                                                onClick={() => handleEditDraft(draft)}
                                            >
                                                {t('news.edit')}
                                            </button>
                                            <button
                                                className="preview-button"
                                                onClick={() => handleDeleteDraft(draft.group_id)}
                                            >
                                                {t('news.delete')}
                                            </button>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </Tab>
            </Tabs>
        </div>
    );
}

const NewsItem = ({item, background, confirmDeleteNews, handleSelectNews, isPreview}) => {
    const contentRef = React.useRef(null);
    const [isOverflowed, setIsOverflowed] = useState(false);
    const {t} = useTranslation();
    const navigate = useNavigate();

    useEffect(() => {
        const current = contentRef.current;
        if (current) {
            const observer = new ResizeObserver(() => {
                setIsOverflowed(current.scrollHeight > current.clientHeight);
            });
            observer.observe(current);
            setIsOverflowed(current.scrollHeight > current.clientHeight);

            return () => {
                observer.disconnect();
            };
        }
    }, [item.description]);

    const formatDate = (createdAt) => {
        if (!createdAt) return '';
        const date = new Date(createdAt);
        if (isNaN(date.getTime())) return '';

        const localeMap = {
            'ua': 'uk',
            'en': 'en',
            'ko': 'ko',
            'fr': 'fr',
            'ru': 'ru',
        };
        const locale = localeMap[i18n.language] || 'en';

        return date.toLocaleString(locale, {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        });
    };

    const getPluralForm = (count, singular, few, many) => {
        const mod10 = count % 10;
        const mod100 = count % 100;

        if (mod10 === 1 && mod100 !== 11) {
            return singular;
        }
        if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) {
            return few;
        }
        return many;
    };

    const calculateReadTime = (text) => {
        if (!text) return `${t('news.readTime', { count: 1 })}`;
        const words = text.split(/\s+/).length;
        const readTime = Math.ceil(words / 200);
        const singular = t('news.readTimeSingular');
        const few = t('news.readTimeFew');
        const many = t('news.readTimeMany');
        const pluralForm = getPluralForm(readTime, singular, few, many);
        return `${readTime} ${pluralForm} ${t('news.forReading')}`;
    };

    const readMore = () => {
        navigate(`/news/${item.id}`);
    };

    return (
        <article className="news-item">
            <div className="news-content">
                <div className="news-thumbnail">
                    <img src={background} alt="news thumbnail" />
                </div>
                <div className="news-data">
                    <div className="news-meta">
                        <span><FontAwesomeIcon icon={faCalendarAlt}/> {formatDate(item.created_at)}</span>
                        <span><FontAwesomeIcon icon={faUser}/> {item.author || 'admin'}</span>
                        <span><FontAwesomeIcon icon={faClock}/> {calculateReadTime(item.description)}</span>
                        {!isPreview && (
                            <>
                                <span>
                                    <button
                                        className="news-edit-button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleSelectNews();
                                        }}
                                        aria-label={t('news.edit')}
                                    >
                                        <FontAwesomeIcon icon={faEdit}/> {t('news.edit')}
                                    </button>
                                </span>
                                <span>
                                    <button
                                        className="news-delete-button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            confirmDeleteNews(item.group_id);
                                        }}
                                        aria-label={t('news.delete')}
                                    >
                                        <FontAwesomeIcon icon={faTrash}/> {t('news.delete')}
                                    </button>
                                </span>
                            </>
                        )}
                        {!isPreview && isOverflowed && (
                            <span className="read-more" onClick={readMore}>
                                <FontAwesomeIcon icon={faBookOpen}/> {t('news.readMore') || 'Read More'}
                                <FontAwesomeIcon className="fa-arrow-right" icon={faArrowRight}/>
                            </span>
                        )}
                    </div>
                    {!isPreview ? (
                        <span className="news-title">
                            <Link to={`/news/${item.id}`}>{item.title}</Link>
                        </span>
                    ) : (
                        <h2 className="news-title">{item.title}</h2>
                    )}
                    <div
                        ref={contentRef}
                        dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(item.description, {FORBID_TAGS: ['img']})
                        }}
                        className="news-description"
                    ></div>
                </div>
            </div>
        </article>
    );
};

export default NewsTab;